'use client';

import { useCallback, useRef } from 'react';
import { noop } from 'lodash';
import Link from 'next/link';
import { number } from '@frameio/i18n';
import { getLink } from '@frameio/shared/routing/getLink';
import type { CopyLinkSourceForAnalytics } from '@frameio/shared/shares/hooks/useCopyShareLink';
import { useCopyShareLink } from '@frameio/shared/shares/hooks/useCopyShareLink';
import { trackingMarksStorage } from '@frameio/utils/storage/appUtilsStorage';
import { MenuButton, MenuGroup, MenuNonInteractiveArea } from '@frameio/vapor';
import {
  IconDuplicate,
  IconEdit,
  IconLink,
  IconMinusCircle,
  IconOpenIn,
  IconPower,
  IconTextCursor,
  IconTrash,
} from '@frameio/vapor-icons';
import TEXT from './generated/TEXT/ShareActionsMenu';

TEXT.DEFINE({
  /** a label for the menu item that copies a link to the clipboard */
  labelCopy: 'Copy Link',
  /** a label for the menu item that deletes a share */
  labelDelete: 'Delete',
  /** a label for the menu item that disables access to a share */
  labelDisableAccess: 'Disable Access',
  /** a label for the menu item that duplicates a share */
  labelDuplicate: 'Duplicate',
  /** a label for the menu item that edits a share */
  labelEditShare: 'Edit Share',
  /** a label for the menu item that enables access to a share */
  labelEnableAccess: 'Enable Access',
  /** a label for the header of a pop-up menu */
  labelHeader: (count: number) => `${number(count)} Shares`,
  /** a label for the menu item that renames a share */
  labelRename: 'Rename',
  /** a label for the menu item that opens a share page */
  labelOpenInNewTab: 'Open Share',
});

export enum ShareActionsMenuItems {
  COPY_LINK = 'COPY_LINK',
  DELETE = 'DELETE',
  DUPLICATE = 'DUPLICATE',
  EDIT_IN_BUILDER = 'EDIT_IN_BUILDER',
  OPEN_IN_NEW_TAB = 'OPEN_IN_NEW_TAB',
  RENAME_SHARE = 'RENAME_SHARE',
  SELECTED_SHARE_COUNT_HEADER = 'SELECTED_SHARE_COUNT_HEADER',
  TOGGLE_ACCESS = 'TOGGLE_ACCESS',
}

export type ShareActionsMenuItemsType = Partial<
  Record<ShareActionsMenuItems, boolean>
>;

export type ShareActionsMenuProps = {
  /**
   * Shares that are synced to a Collection Share cannot be renamed
   */
  canBeRenamed?: boolean;
  /**
   * Determines if users can view the share.
   */
  isShareDisabled: boolean;
  menuItems?: ShareActionsMenuItemsType;
  onDelete?: () => void;
  onDisableAccess?: () => void;
  onDuplicateShare?: () => void;
  onEnableAccess?: () => void;
  onSelectRenameShare?: (shareId?: string) => void;
  projectId: string;
  shareIds: Array<string>;
  shareUrl: string;
} & CopyLinkSourceForAnalytics;

export function ShareActionsMenu({
  actionSource,
  canBeRenamed,
  isShareDisabled,
  menuItems = {
    [ShareActionsMenuItems.COPY_LINK]: true,
    [ShareActionsMenuItems.DELETE]: true,
    [ShareActionsMenuItems.OPEN_IN_NEW_TAB]: true,
    [ShareActionsMenuItems.TOGGLE_ACCESS]: true,
  },
  onDelete = noop,
  onDisableAccess = noop,
  onDuplicateShare = noop,
  onEnableAccess = noop,
  onSelectRenameShare = noop,
  projectId,
  shareIds,
  shareUrl,
}: ShareActionsMenuProps) {
  const copyShareLink = useCopyShareLink();

  const shareCount = shareIds?.length;

  const isShareDisabledRef = useRef(isShareDisabled);

  const trackShareBuilderViewedSource = useCallback(() => {
    trackingMarksStorage.set(
      'share-builder-viewed-source',
      'AllSharesList',
      'session'
    );
  }, []);

  return (
    <>
      {(menuItems.OPEN_IN_NEW_TAB || menuItems.COPY_LINK) && (
        <MenuGroup>
          {menuItems.OPEN_IN_NEW_TAB && (
            <MenuButton
              as="a"
              data-dd-action-name="share-builder.open-in-new-tab"
              href={shareUrl}
              iconBefore={<IconOpenIn />}
              onClick={(e) => {
                /**
                 * If this click isn't stopped,
                 * it will dispatch an event to the target link element
                 * and prevent this link to open in a new tab
                 */
                //
                e.stopPropagation();
              }}
              rel="noopener noreferrer"
              target="_blank"
            >
              {TEXT.labelOpenInNewTab}
            </MenuButton>
          )}
          {menuItems.COPY_LINK && (
            <MenuButton
              data-dd-action-name="share-builder.copy-link"
              data-testid="copy-link"
              iconBefore={<IconLink />}
              onClick={() =>
                copyShareLink({
                  actionSource,
                  shareId: shareIds[0],
                  shareUrl,
                })
              }
            >
              {TEXT.labelCopy}
            </MenuButton>
          )}
        </MenuGroup>
      )}
      {(menuItems.EDIT_IN_BUILDER ||
        (menuItems.RENAME_SHARE && canBeRenamed) ||
        menuItems.DUPLICATE) && (
        <MenuGroup>
          {menuItems.EDIT_IN_BUILDER && (
            <Link
              href={getLink({
                page: 'shareBuilder',
                projectId,
                shareId: shareIds[0],
              })}
              onClick={trackShareBuilderViewedSource}
            >
              <MenuButton
                as="a"
                data-dd-action-name="share-builder.edit-share"
                iconBefore={<IconEdit />}
              >
                {TEXT.labelEditShare}
              </MenuButton>
            </Link>
          )}

          {menuItems.DUPLICATE && (
            <MenuButton
              data-dd-action-name="share-builder.duplicate"
              iconBefore={<IconDuplicate />}
              onClick={onDuplicateShare}
            >
              {TEXT.labelDuplicate}
            </MenuButton>
          )}

          {menuItems.RENAME_SHARE && canBeRenamed && (
            <MenuButton
              data-dd-action-name="share-builder.rename"
              iconBefore={<IconTextCursor />}
              onClick={() => onSelectRenameShare()}
            >
              {TEXT.labelRename}
            </MenuButton>
          )}
        </MenuGroup>
      )}
      {(menuItems.TOGGLE_ACCESS || menuItems.DELETE) && (
        <MenuGroup>
          {shareCount > 1 && menuItems.SELECTED_SHARE_COUNT_HEADER && (
            <MenuNonInteractiveArea>
              {TEXT.labelHeader(shareCount)}
            </MenuNonInteractiveArea>
          )}
          {menuItems.TOGGLE_ACCESS &&
            (isShareDisabledRef.current ? (
              <MenuButton
                data-dd-action-name="share-builder.enable-access"
                data-testid="enable-access"
                iconBefore={<IconPower />}
                onClick={onEnableAccess}
              >
                {TEXT.labelEnableAccess}
              </MenuButton>
            ) : (
              <MenuButton
                data-dd-action-name="share-builder.disable-access"
                data-testid="disable-access"
                iconBefore={<IconMinusCircle />}
                onClick={onDisableAccess}
              >
                {TEXT.labelDisableAccess}
              </MenuButton>
            ))}

          {menuItems.DELETE && (
            <MenuButton
              data-dd-action-name="share-builder.delete"
              data-testid="delete"
              iconBefore={<IconTrash />}
              onClick={onDelete}
              variant="destructive"
            >
              {TEXT.labelDelete}
            </MenuButton>
          )}
        </MenuGroup>
      )}
    </>
  );
}
